/**
 * BI — ХАДГАЛАХ БА ЭКСПОРТЫН ГЭРЭЭ (docs/46 §9 Ү5, docs/31 §4o).
 *
 * ! API ба админ ЭНЭ файлаас л уншина: самбарын хадгалалт (`/me/bi-boards`), нийтлэгдсэн анхдагч
 * ! (`/bi/boards/:domain/published`), нэртэй харагдац (`/me/bi-views`), серверийн Excel (`/bi/export/xlsx`).
 * ! Хоёр тал тусдаа хувилбар бичвэл хязгаар, түлхүүр, томьёоны хамгаалалт нэг дээр нь зөрж, алдаа нь
 * ! чимээгүй гарна (Excel-ийн тоо дэлгэцийнхээс өөр, харагдац «хадгалагдсан» мэт боловч 400).
 *
 * ! Самбарын JSON-ы ХЭЛБЭРИЙН эзэн нь админы `sanitize` (`views/apps/bi/canvas/use-board.ts`). Сервер
 * ! зөвхөн гүехэн шалгана (объект, схемийн дугаар, хэмжээ) — уншихдаа админ дахин цэвэрлэнэ.
 */

/* ─────────────────────────── Домэйн ─────────────────────────── */

/** BI-ийн домэйн — админы `BiDomain`-тэй ИЖИЛ (spec тулгана). Цэсний таван хуудас. */
export const BI_DOMAIN_KEYS = ['sales', 'warehouse', 'delivery', 'finance', 'hr'] as const

export type BiDomainKey = (typeof BI_DOMAIN_KEYS)[number]

export const isBiDomainKey = (value: unknown): value is BiDomainKey =>
  typeof value === 'string' && (BI_DOMAIN_KEYS as readonly string[]).includes(value)

/**
 * ТҮВШИН (D1, Ү7; Ү8 D8-11). Ү5-д бүгд `basic`. Pro ба Менежер нь Basic-ийн НИЙТЛЭГДСЭН самбарыг `id`-ээр нь ишилнэ —
 * хуулбарлахгүй (docs/46 §9 Ү7). Менежер самбар Pro-г ИШЛЭХГҮЙ (Pro хэсэгт сувгаар огтлогдохгүй хавтан бий).
 *
 * ! ДАРААЛАЛ УТГАТАЙ: доод → дээд (`basic` < `manager` < `pro`). Сэлгүүр, `biTierRank` энэ дарааллыг уншина.
 * ! Ү8 (docs/31 §4u): `manager` НЭМЭГДСЭН — DB-д шилжилт хэрэггүй (`bi_*_boards.tier` TEXT, CHECK-гүй).
 */
export const BI_BOARD_TIERS = ['basic', 'manager', 'pro'] as const

/** Ү8 — BI-ийн түвшний толь (самбарын түвшинтэй ИЖИЛ жагсаалт; D8-3). */
export const BI_TIERS = BI_BOARD_TIERS

export type BiBoardTier = (typeof BI_BOARD_TIERS)[number]

/** Ү8 — `BiBoardTier`-ийн өргөн нэр (API-ийн хандалт, админы горим). */
export type BiTier = BiBoardTier

/* ─────────────────────────── Самбар (хувийн + нийтлэгдсэн) ─────────────────────────── */

/**
 * САМБАРЫН JSON-ы СХЕМИЙН ДУГААР — админы `STORAGE_VERSION`-тэй ИЖИЛ.
 *
 * ! Сервер өөр дугаартай PUT-ийг 400-аар буцаана. Дугаар өсгөх нь ЗӨВХӨН админы `migrate()`-тэй хамт.
 */
export const BI_BOARD_SCHEMA_VERSION = 1

/** Самбарын төлөвийн цуваачилсан (UTF-8 JSON) дээд хэмжээ, байтаар. Хэтэрвэл 413. */
export const BI_BOARD_STATE_MAX_BYTES = 64 * 1024

/**
 * Самбарын төлөв — админы `Board` (`views/apps/bi/types.ts`). Энд `unknown`-ийн объект: сервер хэлбэрийг
 * ЭЗЭМШДЭГГҮЙ, уншсан тал `sanitize` хийнэ.
 */
export type BiBoardState = Record<string, unknown>

/** ХУВИЙН самбар — хэрэглэгч × домэйн нэг мөр. */
export type BiPersonalBoard = {
  id: string
  domain: BiDomainKey
  state: BiBoardState
  schemaVersion: number

  /** Өөдрөг зэрэгцээ хяналтын дугаар — 1-ээс эхэлж PUT бүрд +1. */
  version: number

  /**
   * Энэ самбар аль НИЙТЛЭГДСЭН хувилбараас эхэлсэн бэ. `null` = seed-ээс (эсвэл нийтлэгдсэнээс өмнө).
   * Нийтлэгдсэн `version` үүнээс их бол админ «шинэчлэгдсэн анхдагч бий · авах» гэж ХААЛТГҮЙ сануулна.
   */
  basedOnPublishedVersion: number | null

  /** ISO-8601 UTC. */
  updatedAt: string
}

/** НИЙТЛЭГДСЭН анхдагч — домэйн × түвшин нэг мөр (Q6). `id` тогтвортой (нийтлэлт цуцлагдсан ч) — Ү7 ишилнэ. */
export type BiPublishedBoard = {
  id: string
  domain: BiDomainKey
  tier: BiBoardTier
  state: BiBoardState
  schemaVersion: number

  /** Нийтлэлт/цуцлалт бүрд +1, ХЭЗЭЭ Ч дахин ашиглагдахгүй (цуцалсны дараах нийтлэлт ч өснө). */
  version: number

  /** ISO-8601 UTC. */
  publishedAt: string
  publishedBy: { id: string; name: string } | null
}

/**
 * `GET /me/bi-boards/:domain` — нэг дуудлагаар шийдвэрт хэрэгтэй бүгд: хувийн → нийтлэгдсэн → seed.
 *
 * ! Бие ҮРГЭЛЖ объект (`null` биш) — `table-preferences.service.ts`-ийн «200, 0 байт» сургамж.
 */
export type BiBoardsResponse = {
  domain: BiDomainKey
  personal: BiPersonalBoard | null
  published: BiPublishedBoard | null

  /**
   * Ү7 (docs/31 §4t, D7-3) — `?tier=` (анхдагч `basic`). `tier=pro` хариунд `personal`/`published` нь Pro мөр (`tier='pro'`),
   * `basic` нь Pro самбарын ИШЛЭХ эх — одоо НИЙТЛЭГДСЭН Basic самбар (`null` = нийтлэгдээгүй → админ Basic seed). Basic хариунд
   * `tier` нь `'basic'` эсвэл байхгүй (Ү5-ын сервер), `basic` байхгүй.
   */
  tier?: BiBoardTier
  basic?: BiPublishedBoard | null
}

/** `PUT /me/bi-boards/:domain` бие. */
export type BiSavePersonalBoardBody = {
  state: BiBoardState
  schemaVersion: number

  /** Үйлчлүүлэгчийн сүүлд харсан `version`; мөр байхгүй гэж үзвэл `null`. Зөрвөл 409. */
  expectedVersion: number | null
  basedOnPublishedVersion: number | null
}

/** `PUT /bi/boards/:domain/published` бие. */
export type BiPublishBoardBody = {
  state: BiBoardState
  schemaVersion: number

  /** Одоо НИЙТЛЭГДСЭН байгаа `version`; нийтлэгдсэн зүйлгүй гэж үзвэл `null`. Зөрвөл 409. */
  expectedVersion: number | null
}

/** 409-ийн `code`. */
export const BI_CONFLICT_CODES = {
  personal: 'BI_BOARD_VERSION_CONFLICT',
  published: 'BI_PUBLISHED_VERSION_CONFLICT',
  viewName: 'BI_VIEW_NAME_TAKEN',
  viewLimit: 'BI_VIEW_LIMIT'
} as const

/** 409 — хувийн самбарын хувилбар зөрсөн. `current` = серверийн ОДООГИЙН мөр (устгагдсан бол `null`). */
export type BiPersonalBoardConflict = {
  statusCode: 409
  error: 'Conflict'
  code: typeof BI_CONFLICT_CODES.personal
  message: string
  current: BiPersonalBoard | null
}

/** 409 — нийтлэгдсэн самбарын хувилбар зөрсөн (өөр хүн нийтэлсэн/цуцалсан). */
export type BiPublishedBoardConflict = {
  statusCode: 409
  error: 'Conflict'
  code: typeof BI_CONFLICT_CODES.published
  message: string
  current: BiPublishedBoard | null
}

/** 413 — самбарын төлөв хэт том. */
export type BiBoardTooLarge = {
  statusCode: 413
  error: 'Payload Too Large'
  code: 'BI_BOARD_TOO_LARGE'
  message: string
  limit: number
  size: number
}

/* ─────────────────────────── Нэртэй харагдац ─────────────────────────── */

/** Хэрэглэгч × домэйн бүрд дээд тоо. */
export const BI_VIEW_MAX_PER_DOMAIN = 50

export const BI_VIEW_NAME_MIN = 1
export const BI_VIEW_NAME_MAX = 60

/**
 * Нэг утгын дээд урт (facet-ийн олон id таслалаар). docs/31 §4v.5: бараа/харилцагчийн facet 100 uuid хүртэл
 * (`BI_ENTITY_LOOKUP_MAX_IDS`) = 100 × 36 + 99 таслал = 3699 — 1000 байхад ~27 uuid-аас хойш админ хадгалахдаа шүүлтийг
 * ЧИМЭЭГҮЙ хасдаг байсан (`views-model.ts`). `query` нь Json багана — уртын хязгааргүй.
 */
export const BI_VIEW_VALUE_MAX = 3800

/**
 * ХАРАГДАЦАД ХАДГАЛАГДАХ URL ТҮЛХҮҮР — домэйн бүрээр ЦАГААН ЖАГСААЛТ.
 *
 * ! = админы `BI_PERIOD_KEYS` + `BI_FACETS[domain]`-ийн түлхүүр (`filters/bi-facets.ts`). Админы spec ИЖИЛ
 * ! эсэхийг барина. Шинэ facet нэмэхэд ЭНД Ч нэм — эс бөгөөс сервер 400 өгч харагдац хадгалагдахгүй (ил).
 */
const BI_VIEW_PERIOD_KEYS = ['from', 'to', 'cmp', 'grain'] as const

export const BI_VIEW_QUERY_KEYS: Readonly<Record<BiDomainKey, readonly string[]>> = {
  sales: [
    ...BI_VIEW_PERIOD_KEYS,
    'warehouse',
    'businessRegion',
    'deliveryZone',
    'salesChannel',
    'outletType',
    'partnerType',
    /*
     * ! docs/31 §4w (2026-09-27): `manager` («Үндсэн менежер») ХАСАГДСАН (`BI_RETIRED_FACET_KEYS`) — хуучин харагдац хүчинтэй
     * ! хэвээр нээгдэхийн тулд ЭНД ҮЛДЭНЭ; админ нээхдээ нэг удаа цэвэрлээд мэдэгдэл + санал болгох багийг харуулна, дахин
     * ! хадгалахад бичигдэхгүй. API-д ирвэл 400 `BI_FILTER_RETIRED`. `salesManager` = «Борлуулалтын баг (маршрут)».
     */
    'manager',
    'salesManager',
    'brand',
    'productGroup',
    'manufacturer',
    /* docs/31 §4v.5 (drill-down): async хайлттай бараа / худалдан авагч — `DETAIL_FACETS`-ийн төгсгөлийн хоёр. */
    'product',
    'customer'
  ],
  /* ! Ү6b (docs/31 §4r): `/warehouse/batches`, `/warehouse/picking`-ийн `gateId` + `/warehouse/returns`-ийн `kind`. `status` БИШ —
   * ! агуулахын жагсаалт бүрд өөр утгатай (багц / бэлтгэл / буцаалт / fulfilment). */
  warehouse: [...BI_VIEW_PERIOD_KEYS, 'gateId', 'kind'],

  /* ! Ү6a (docs/31 §4q): `/delivery/runs`-ийн `status`, `vehicleId` + `/warehouse/returns`-ийн `kind`. */
  delivery: [...BI_VIEW_PERIOD_KEYS, 'status', 'vehicleId', 'kind'],
  /* ! Ү6b (docs/31 §4r): санхүүд facet АЛГА — ↗ жагсаалтуудын шүүлт клиент талын мөрийн шүүлт (суваг, насжилт), BI-ийн
   * ! хавтангуудад нэг ч түлхүүр бүгдэд ЯГ хамаардаггүй; задаргааны мөрийн ↗ нь шүүлтийг хүргэнэ. */
  finance: [...BI_VIEW_PERIOD_KEYS],

  /* ! Ү6a (docs/31 §4q): `/hr/employees`-ийн `EMPLOYEE_FACETS`-ийн дөрөв (`status`-гүй). */
  hr: [...BI_VIEW_PERIOD_KEYS, 'departmentId', 'organizationId', 'jobPositionId', 'workLocationId']
}

/** Харагдацын URL төлөв — зөвшөөрөгдсөн түлхүүр → мөр. */
export type BiViewQuery = Record<string, string>

export type BiNamedView = {
  id: string
  domain: BiDomainKey
  name: string
  query: BiViewQuery

  /** ISO-8601 UTC. */
  createdAt: string
  updatedAt: string
}

/** `GET /me/bi-views/:domain`. Нэрээр эрэмбэлсэн (`localeCompare('mn')`). */
export type BiViewsResponse = {
  domain: BiDomainKey
  views: BiNamedView[]
  limit: number
}

/** `POST /me/bi-views/:domain`. */
export type BiCreateViewBody = { name: string; query: BiViewQuery }

/** `PATCH /me/bi-views/:domain/:id` — нэр солих ба/эсвэл «одоогийн төлвөөр шинэчлэх». */
export type BiUpdateViewBody = { name?: string; query?: BiViewQuery }

/**
 * ХАРАГДАЦЫН НЭРИЙН ХАРЬЦУУЛАХ ТҮЛХҮҮР — давхардлыг том/жижиг үсэг, илүү зайгүйгээр шалгана.
 * Сервер `name_key` баганад ЭНИЙГ бичнэ; админ урьдчилан шалгахдаа мөн ЭНИЙГ.
 */
export const biViewNameKey = (name: string): string => name.normalize('NFC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('mn')

/**
 * ХАРАГДАЦЫН URL ТӨЛВИЙН АЛДАА — хоосон массив = хүчинтэй.
 *
 * ! Сервер (400) ба админ (хадгалахаас өмнө) ИЖИЛ функцээр. Утгын ДОТООД хэлбэрийг (огноо, facet id)
 * ! шалгахгүй: харагдацыг хэрэглэхэд хуудасны өөрийн задлагч (`use-bi-period`, `use-bi-filters`) цэвэрлэнэ.
 */
export const biViewQueryProblems = (domain: BiDomainKey, query: unknown): string[] => {
  if (!query || typeof query !== 'object' || Array.isArray(query)) return ['query: объект биш']

  const allowed = new Set(BI_VIEW_QUERY_KEYS[domain])
  const problems: string[] = []

  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (!allowed.has(key)) problems.push(`query.${key}: зөвшөөрөгдөөгүй түлхүүр`)
    else if (typeof value !== 'string') problems.push(`query.${key}: мөр биш`)
    else if (value.length === 0 || value.length > BI_VIEW_VALUE_MAX) problems.push(`query.${key}: урт 1–${BI_VIEW_VALUE_MAX}`)
    // eslint-disable-next-line no-control-regex
    else if (/[\u0000-\u001f\u007f]/.test(value)) problems.push(`query.${key}: удирдах тэмдэгт`)
  }

  return problems
}

/* ─────────────────────────── Экспорт (Excel / CSV / TSV) ─────────────────────────── */

/**
 * СЕРВЕРИЙН EXCEL-ИЙН ХЯЗГААР. Хэтэрвэл 400 (таслахгүй — таславал Excel дэлгэцээс ЧИМЭЭГҮЙ дутуу).
 */
export const BI_EXPORT_LIMITS = {
  tiles: 40,
  rowsPerTile: 1000,
  columnsPerTile: 20,

  /**
   * НИЙТ НҮД (мөр × багана, бүх хавтан). exceljs ном бүхлээр санах ойд бүтээнэ: 800k нүд (40×1000×20) ≈ +600 MB
   * RSS — 1 GB-ийн pm2 хязгаартай нэг процессыг унагана. 200k ≈ +280 MB (хэмжилт docs/31 §4p). Сервер мөн
   * процесс даяар НЭГ экспорт зэрэг бүтээнэ (`BI_EXPORT_CONCURRENCY`).
   */
  cellsTotal: 200_000,
  stringLength: 500,
  filters: 30,
  valuesPerFilter: 200
} as const

/**
 * Эрхийн шаардлага: мөр = тэр эрх заавал (AND), дотоод массив = жагсаасны АЛЬ НЭГ (any-of). Сервер
 * (`BiExportController`) ба админ (`use-board-export.ts`) хоёулаа `biPermissionsMissing`-ээр уншина.
 */
export type BiPermissionRequirement = string | readonly string[]

/** Дутуу шаардлага (any-of нь `a эсвэл b` гэж нэрлэгдэнэ) — хоосон = хангалттай. */
export const biPermissionsMissing = (
  required: readonly BiPermissionRequirement[],
  held: (permission: string) => boolean
): string[] =>
  required.flatMap(requirement =>
    typeof requirement === 'string'
      ? held(requirement)
        ? []
        : [requirement]
      : requirement.some(held)
        ? []
        : [requirement.join(' эсвэл ')]
  )

/** Шаардлагын бүх эрхийн түлхүүр (модуль унтраалттай эсэхийг шалгахад). */
export const biPermissionKeys = (required: readonly BiPermissionRequirement[]): string[] =>
  required.flatMap(requirement => (typeof requirement === 'string' ? [requirement] : [...requirement]))

/**
 * EXCEL ЭКСПОРТЫН НЭМЭЛТ ЭРХ домэйн бүрээр — `bi.boards:show`-оос ГАДНА (Ү6a, docs/31 §4q).
 *
 * ! Q3 (docs/46 §11): борлуулалтын хавтан мөрийн үнэ/дүн агуулдаг тул `erp.reports:list`. ХН, түгээлтийн хавтан нь
 * ! ЗӨВХӨН нэгтгэл (нэр, регистр, үнэ алга) бөгөөд хавтан бүрийн тоо эх эрхээр (`hr.employees:list`,
 * ! `delivery.runs:list` …) аль хэдийн татагдсан — эрхгүй хавтан `omitted: forbidden`. Тиймээс нэмэлт эрхгүй;
 * ! `erp.reports:list` шаардвал ХН-ийн менежер өөрийн самбараа экспортлож чадахгүй. Сервер (`BiExportController`) ба
 * ! админ (`use-board-export.ts`) хоёулаа ЭНИЙГ.
 */
export const BI_EXPORT_PERMISSIONS: Readonly<Record<BiDomainKey, readonly BiPermissionRequirement[]>> = {
  /*
   * ! Ү7 (docs/31 §4t, шалгалтын №1): жагсаалтын эрх ЭСВЭЛ зөвхөн нэгтгэлийн эрх (`erp.reports:aggregate`) — хавтангийн
   * ! тоо тэр нэгтгэлийн цэгээс татагдсан; мөнгийг `biExportTierProblem` (Pro) тусад нь хаана.
   */
  sales: [['erp.reports:list', 'erp.reports:aggregate']],

  /*
   * ! Ү6b (docs/31 §4r, D0): агуулах, санхүү — НЭМЭЛТ ЭРХГҮЙ (ХН/түгээлтийн адил). Хавтан бүрийн тоо ЭХ жагсаалтын эрхээр
   * ! аль хэдийн татагдсан (`warehouse.*:list`, `erp.warehouses:list`, `erp.credit:list`, `erp.sales:list`); эрхгүй хавтан
   * ! `omitted: forbidden`. Экспорт нь хэрэглэгчийн ӨӨРИЙН дэлгэцийг буулгана — ↗ жагсаалтаас ИЛҮҮ юм агуулахгүй (нэгтгэл,
   * ! Top-N харилцагч нь `/erp/credit`-д харагддаг мөр). `erp.reports:list` шаардвал агуулахын менежер, зээлийн ажилтан
   * ! өөрийн самбараа экспортлож чадахгүй — энэ нь хамгаалалт биш, саад.
   */
  warehouse: [],
  delivery: [],
  finance: [],
  hr: []
}

/** Процесс даяар зэрэг бүтээх Excel-ийн тоо; хүлээх дараалал ба хугацаа — хэтэрвэл 503 + `Retry-After`. */
export const BI_EXPORT_CONCURRENCY = { running: 1, waiting: 3, waitMs: 20_000, retryAfterSeconds: 15 } as const

/** Нийт нүдний тоо — сервер ба админ НЭГ дүрмээр (`rows.length × columns.length`). */
export const biExportCellCount = (tiles: readonly { table: { columns: readonly unknown[]; rows: readonly unknown[] } | null }[]): number =>
  tiles.reduce((sum, tile) => sum + (tile.table ? tile.table.rows.length * tile.table.columns.length : 0), 0)

/**
 * ! Ү6b (docs/31 §4r «Жишээ давхаргыг хасах»): 1 → 2 — `BiExportTile.sample` ба `trust.sample` ХАСАГДАВ, `omitted.kind`-д
 * ! `retired` · `unavailable` нэмэгдэв. Хуучин (1) ачааг сервер 400 «Хуудсаа шинэчилнэ үү»-гээр буцаана (хуучин таб).
 */
export const BI_EXPORT_SCHEMA_VERSION = 2

/**
 * Нүдний нэгж → Excel-ийн тооны формат (сервер) ба дэлгэцийн `formatMetricValue` (админ).
 *
 * ! `percent` нь ХУВИЙН ТОО (12.3 = 12.3%), бутархай БИШ — дэлгэцтэй ИЖИЛ тоо. Excel-д `0.0"%"` форматаар
 * ! (`0.0%` БИШ — тэр нь 100-гаар үржүүлнэ). `text` = тоон бус багана.
 */
export const BI_EXPORT_UNITS = ['mnt', 'count', 'qty', 'percent', 'day', 'text'] as const

export type BiExportUnit = (typeof BI_EXPORT_UNITS)[number]

export type BiExportColumn = {
  key: string

  /** ОРЧУУЛСАН гарчиг (дэлгэцийн толгойтой ижил). */
  label: string
  unit: BiExportUnit
}

/** `null` = утгагүй («—»), ТЭГ БИШ. Тоо заавал төгсгөлөг (`Number.isFinite`). */
export type BiExportCell = string | number | null

export type BiExportTrust = 'verified' | 'suspect' | 'blocked'

/**
 * ХҮСНЭГТГҮЙ ХАВТАНГИЙН ШАЛТГААН — «Тойм»-д шалтгаантай нь жагсаагдана, ЧИМЭЭГҮЙ алгасахгүй.
 * `blocked` хаалттай хэмжигдэхүүн · `unknown` бүртгэлд алга · `forbidden` эрх/хүрээ · `offline` 1С тасарсан ·
 * `invalid` хүсэлт буцаагдсан · `loading` ачаалж дуусаагүй · `empty` өгөгдөлгүй (төлөвлөгөөгүй, харьцуулалт хэрэгтэй) ·
 * Ү6b: `retired` хэмжигдэхүүн хасагдсан (хадгалсан самбарт үлдсэн түлхүүр) · `unavailable` домэйны тэжээл холбогдоогүй ·
 * Ү7: `pro` Pro эрх шаардлагатай (Basic горимын түгжээтэй хавтан/хэсэг).
 */
export const BI_EXPORT_OMIT_KINDS = [
  'blocked',
  'unknown',
  'forbidden',
  'offline',
  'invalid',
  'loading',
  'empty',
  'retired',
  'unavailable',

  /* ! Ү7 (docs/31 §4t): Basic горимд Pro хэмжигдэхүүн (түгжээтэй карт) — гарчиг л, тоо ХЭЗЭЭ Ч үгүй. Сервер Basic ачаанд
     ! `mnt` багана/гарчигтай `omitted` биш хавтныг 403 `BI_PRO_REQUIRED` (`biExportTierProblem`). */
  'pro'
] as const

export type BiExportOmitKind = (typeof BI_EXPORT_OMIT_KINDS)[number]

export type BiExportTile = {

  /** Хавтангийн id (амьд «Борлуулалт» хэсгийн дэд хавтан: `<tile.id>:<revenue|documents|avgTicket>`). */
  id: string

  /** ОРЧУУЛСАН гарчиг — дэлгэц дээрхтэй ижил (`tile.title ?? shortLabel`). */
  title: string
  metricKey: string
  trust: BiExportTrust

  /** ОРЧУУЛСАН итгэлийн тайлбар (`bi.metric.<key>.short` эсвэл ерөнхий). Хоосон байж болно. */
  trustNote: string

  /**
   * ОРЧУУЛСАН эх сурвалжийн мөр («1С ERP · 14:05-ийн байдлаар · кэш»). Хүснэгтгүй (`omitted`) хавтанд хоосон.
   * ! Ү6b: ЖИШЭЭ өгөгдөл АЛГА — хавтан бүрийн тоо амьд эх сурвалжаас, эс бөгөөс `omitted` шалтгаантай.
   */
  source: string

  /** ОРЧУУЛСАН нэр — энэ хавтанд хамаараагүй идэвхтэй шүүлт («ШҮҮЛТ ХАМААРАХГҮЙ»). */
  ignoredFilters: string[]

  /** Гарчгийн тоо (`headlineOf`) — дэлгэцийн том тоо. */
  headline: { value: number | null; unit: BiExportUnit } | null

  /** ОРЧУУЛСАН өөрчлөлтийн chip («▲4.1%», «▼0.6 п.п.»). */
  delta: string | null

  /** `MetricData.note` — гарчгийн доорх тайлбар. */
  note: string | null

  /** Хүснэгт — дэлгэц/хэвлэлийн `TileTable`-ийн МӨРҮҮД (таслалгүй, бүгд). `null` бол `omitted` заавал. */
  table: { columns: BiExportColumn[]; rows: BiExportCell[][] } | null
  omitted: { kind: BiExportOmitKind; reason: string } | null
}

export type BiExportPeriod = { from: string; to: string; label: string }

/** `POST /bi/export/xlsx` бие — ДЭЛГЭЦИЙН төлвөөс (дахин асуулга ХИЙХГҮЙ, docs/31 §4o). */
export type BiExportPayload = {
  schemaVersion: typeof BI_EXPORT_SCHEMA_VERSION
  domain: BiDomainKey

  /** Серверийн шошгоны хэл («Тойм», «Хугацаа» …). */
  locale: 'mn' | 'en'

  /** ОРЧУУЛСАН хуудасны гарчиг («Борлуулалт»). */
  title: string

  /** Серверийн цонх (амьд хариу ирсэн бол), эс бөгөөс клиентийн — толгойн `periodCaption`-тэй ИЖИЛ эх. */
  period: BiExportPeriod
  compare: BiExportPeriod | null

  /** Идэвхтэй шүүлт — ОРЧУУЛСАН нэр, id биш. */
  filters: { label: string; values: string[] }[]

  /** Серверийн хүрээ (`applied`/`dropped`) — ОРЧУУЛСАН мөр. Хүрээгүй хэрэглэгч `null`. */
  scope: { applied: string[]; dropped: string[] } | null

  /**
   * Итгэлийн хураангуй — рэйлийн «Өгөгдлийн байдал»-тай ижил тоо. Хасагдсан (`retired`) ба бүртгэлд алга хавтан
   * итгэлийн түвшинд ОРОХГҮЙ — `tiles[].omitted`-оор («Тойм»-д шалтгаантай).
   */
  trust: { verified: number; suspect: number; blocked: number; ignored: number }

  paper: 'A4' | 'A3'
  orientation: 'portrait' | 'landscape'
  tiles: BiExportTile[]
}

/** `POST /bi/export/xlsx`-ийн бие — `payload` дотор (ValidationPipe `whitelist` дотор талыг хөндөхгүй). */
export type BiExportRequestBody = { payload: BiExportPayload }

/** Excel `pageSetup.paperSize` — A4 = 9, A3 = 8. */
export const BI_EXPORT_PAPER_SIZE = { A4: 9, A3: 8 } as const

/* ─────────────────────────── Томьёоны хамгаалалт ─────────────────────────── */

/**
 * ХҮСНЭГТИЙН ТОМЬЁО ОРУУЛАХ ХАЛДЛАГА (CSV/formula injection) — эдгээрээр ЭХЭЛСЭН мөрийг Excel томьёо гэж
 * уншиж болно: `=`, `+`, `-`, `@`, таб, CR.
 */
export const BI_FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'] as const

/**
 * МӨРИЙГ ТОМЬЁО БОЛОХООС ХАМГААЛНА — урд нь `'` нэмнэ (OWASP). Тоо биш, ЗӨВХӨН мөрөнд.
 *
 * ! Сервер (xlsx) ба админ (CSV/TSV) НЭГ функцээр. Сөрөг ТОО `-5` нь тоон нүд тул хөндөгдөхгүй; «-5» гэсэн
 * ! МӨР (жишээ нь нэр) л хамгаалагдана.
 */
export const neutralizeSpreadsheetText = (text: string): string =>
  (BI_FORMULA_PREFIXES as readonly string[]).some(prefix => text.startsWith(prefix)) ? `'${text}` : text
